/**
 * View-model types for the admin Notification Setup grid.
 *
 * The wire types live in `@/lib/api` ({@link NotificationEventType},
 * {@link NotificationSettings}). These are the *screen's* shapes — one flat
 * row per event with the shipped defaults and the tenant's effective config
 * side by side, which is all the grid needs to render a row, decide whether it
 * is overridden, and work out what "reset to platform default" would restore.
 *
 * Severity is deliberately absent. An admin configures *how often* they hear
 * about an event, not how urgent someone else decided it was; the notification
 * centre still styles its rows by severity, but nothing on this screen does.
 *
 * @link ./catalogue for the parser that produces them from the API payload.
 */
import type {
  NotificationChannel,
  NotificationFrequency,
  NotificationScopeSource,
  NotificationTenantDefaultFrequency,
} from '../../../../lib/api';
import type { EventReach } from './eventReach';

/** Every channel the grid renders a column for, in column order. */
export const SETUP_CHANNELS: readonly NotificationChannel[] = ['bell', 'email', 'digest'];

/** Column labels for {@link SETUP_CHANNELS}. */
export const CHANNEL_LABELS: Record<NotificationChannel, string> = {
  bell: 'Bell',
  email: 'Email',
  digest: 'Digest',
};

/**
 * Cadence labels.
 *
 * These win over the labels on the wire for the five values we know. The API
 * sends "Daily digest" / "Never email (in-app only)", which read badly in a
 * narrow column sitting next to a *Digest* channel column — "Daily digest" in
 * the Frequency column next to a Digest tick is a genuine ambiguity. A value we
 * do not recognise keeps whatever label the API gave it, so a cadence added
 * later still renders with words rather than a slug.
 */
export const FREQUENCY_LABELS: Record<NotificationFrequency, string> = {
  immediate: 'Immediate',
  hourly: 'Hourly',
  daily: 'Daily',
  weekly: 'Weekly',
  off: 'Off',
};

/** Label for the tenant-wide "no blanket cadence" option. */
export const TENANT_INHERIT_LABEL = "Each event's own default";

/** One-line explanation per cadence, shown under the selector. */
export const FREQUENCY_HINTS: Record<NotificationFrequency, string> = {
  immediate: 'Sent the moment the event happens.',
  hourly: 'Batched and sent at the top of each hour.',
  daily: 'Batched into one email a day.',
  weekly: 'Batched into one email a week.',
  off: 'Bell only — this event is never emailed.',
};

/**
 * What a row delivers once the company's one daily digest carries its email.
 * The bell still fires at once; only the email waits.
 */
export const ONE_DAILY_SENTENCE = 'Email goes in the daily digest at 07:00';

/** The same row while batched mail is off: the digest does not catch up later. */
export const ONE_DAILY_OFF_SENTENCE = 'Bell only while digest emails are off';

/** The heading of the email kill switch's warning, on both tabs. */
export const EMAIL_STOPPED_TITLE = 'All notification email stopped';

/** The email kill switch's warning, on both tabs. */
export const EMAIL_STOPPED_TEXT =
  'Nobody in this tenant is being emailed or digested about anything. In-app '
  + 'bells still work; invitations and password resets are unaffected.';

/** Cadence order for the selector, loudest first. */
export const FREQUENCY_ORDER: readonly NotificationFrequency[] = [
  'immediate',
  'hourly',
  'daily',
  'weekly',
  'off',
];

/**
 * Sentinel both frequency `<Select>`s use for "inherit from the level above" —
 * a row deferring to the tenant default, or the tenant deferring to each
 * event's platform default.
 *
 * Radix `SelectItem` throws on an empty-string value, and `null` is not a
 * `Select` value, so the inherit option needs a real string of its own.
 */
export const INHERIT_FREQUENCY = '__inherit__';

/** `off` blocks email and digest outright, whatever the channel ticks say. */
export const isEmailSilenced = (frequency: NotificationFrequency): boolean =>
  frequency === 'off';

/**
 * Whether this combination of ticks and cadence produces any email at all.
 *
 * `hourly` used to count as silent: the cadence was selectable and the backend
 * stamped rows for it, but no run collected them, so nothing was delivered. It
 * delivers now — the send logs carry a per-hour period key and all three
 * senders claim the cadence — so it is treated like any other batched cadence.
 */
export const deliversNoEmail = (
  channels: NotificationChannel[],
  frequency: NotificationFrequency,
): boolean =>
  frequency === 'off'
  || !(channels.includes('email') || channels.includes('digest'));

/**
 * One plain sentence stating what this row will actually do.
 *
 * The grid's central problem was that its two mail columns were
 * indistinguishable: Email and Digest produced byte-identical behaviour at
 * every cadence, so an admin could not tell what a tick bought them. Now they
 * differ — on a batched cadence, Email gives the event type its own mail with
 * a line per event, Digest folds it into the shared summary as a count — and
 * the row has to say which of the two it is doing.
 *
 * Every cadence now describes a real delivery. `hourly` used to be the one
 * exception — it stated the gap rather than implying a delivery, because
 * nothing collected its rows — and it reads like the other batched cadences
 * now that it does.
 *
 * @param channels - the channels ticked on this row (not the surfaces the
 *   backend resolves them to).
 * @param frequency - the cadence this row runs on right now.
 * @param others - what baseline routing gives people reached without a named
 *   buyer or owner on this row ({@link othersDelivery}), or `null`.
 * @param oneDaily - what the one daily digest does to this row
 *   ({@link oneDailyDelivery}). Either answer replaces the cadence wording.
 * @returns the sentence the frequency cell's tooltip shows.
 */
export const describeDelivery = (
  channels: NotificationChannel[],
  frequency: NotificationFrequency,
  others: OthersDelivery = null,
  oneDaily: OneDailyDelivery = null,
): string => {
  if (!(channels.includes('email') || channels.includes('digest'))) {
    return 'Bell only — no email is sent.';
  }
  if (frequency === 'off') return FREQUENCY_HINTS.off;
  if (oneDaily === 'digest') return ONE_DAILY_SENTENCE;
  if (oneDaily === 'bell') return ONE_DAILY_OFF_SENTENCE;
  // Baseline routing holds only an immediate or hourly row to one a day, and
  // only for people reached without a named buyer or owner (EffectiveTemplate
  // .department_daily). A weekly or daily row is never brought forward.
  const note = othersNote(frequency, others);
  if (frequency === 'immediate') return `One email as each event happens.${note}`;
  const period = frequency === 'hourly'
    ? 'hourly'
    : (frequency === 'weekly' ? 'weekly' : 'daily');
  // Matches the backend's tie-break: ticking both means Email, because one row
  // cannot be delivered twice and Email is the more deliberate choice.
  return channels.includes('email')
    ? `Its own ${period} email, listing each event.${note}`
    : `Counted in the ${period} summary.${note}`;
};

/**
 * What baseline routing gives people a row reaches without a named buyer or
 * owner: one email a day, or the bell only while batched mail is switched off.
 * `daily` / `bell` / `null` (no note).
 */
export type OthersDelivery = 'daily' | 'bell' | null;

const OTHERS_NOTES: Record<'daily' | 'bell', string> = {
  daily: ' Anyone reached without a named buyer or owner gets one email a day instead.',
  bell: ' Anyone reached without a named buyer or owner gets the bell only while digest emails are off.',
};

/**
 * The {@link OthersDelivery} sentence for a cadence, or `''`: the rule only
 * holds an immediate or hourly cadence to one a day.
 *
 * @param frequency - the cadence a row, or the tenant default, runs on.
 * @param others - what baseline routing gives the rest, or `null`.
 * @returns the sentence to append, with its leading space.
 */
export const othersNote = (
  frequency: NotificationFrequency,
  others: OthersDelivery,
): string => (others && (frequency === 'immediate' || frequency === 'hourly')
  ? OTHERS_NOTES[others]
  : '');

/**
 * Whether baseline routing's once-a-day rule applies to this row, and how.
 *
 * Mirrors the backend: it applies only while the row has no frequency of its
 * own (an admin's per-notification choice wins) and still has its bell — the
 * daily mail is collected from the bell row, so a row without one keeps its
 * own delivery. With batched mail switched off it is the bell only.
 *
 * @param event - the catalogue row.
 * @param config - its current config, staged edits included.
 * @param channels - the channels the row will actually use.
 * @param digestEnabled - the batched-mail master switch.
 * @returns the {@link OthersDelivery} for {@link describeDelivery}.
 */
export const othersDelivery = (
  event: Pick<SetupEvent, 'baselineDaily'>,
  config: Pick<EventConfig, 'frequency'>,
  channels: NotificationChannel[],
  digestEnabled: boolean,
): OthersDelivery => {
  if (!event.baselineDaily || config.frequency !== null || !channels.includes('bell')) return null;
  return digestEnabled ? 'daily' : 'bell';
};

/**
 * What the one daily digest does: `digest` carries the email, `bell` means events
 * reach people by bell only because digest emails are off, and `null` means it
 * changes nothing.
 */
export type OneDailyDelivery = 'digest' | 'bell' | null;

/**
 * What the one daily digest does for this tenant right now, from its switches.
 *
 * The digest rides on the bell row, so with the Bell off the backend keeps each
 * event's own email and the digest changes nothing, whatever the mail switches
 * say. With the Bell on, it depends on whether batched mail can go out at all.
 *
 * @param oneDailyDigest - `meta.one_daily_digest`: the company is on the digest.
 * @param batchedMail - the Daily digest and Email switches are both on.
 * @param bellEnabled - the Bell switch.
 * @returns the {@link OneDailyDelivery}. `digest` is what the screen calls
 *   `digestActive`.
 */
export const tenantOneDaily = (
  oneDailyDigest: boolean,
  batchedMail: boolean,
  bellEnabled: boolean,
): OneDailyDelivery => {
  if (!oneDailyDigest || !bellEnabled) return null;
  return batchedMail ? 'digest' : 'bell';
};

/**
 * Whether the one daily digest governs this row right now, and how.
 *
 * Mirrors the backend's `one_daily_digest.carries` and `daily_channels`, in both
 * switch states: the digest rides on the bell row, so a row with no bell, or no
 * mail, keeps its own delivery. `event.oneDaily` is read off the catalogue, so
 * it is checked against what is on screen now. A staged pin takes the row out of
 * the digest, and a tenant default moved to Weekly since that load is never
 * brought forward.
 *
 * @param event - the catalogue row.
 * @param config - its current config, staged edits included.
 * @param channels - the channels the row will actually use.
 * @param frequency - the cadence it runs on right now.
 * @param tenant - the tenant's own state, from {@link tenantOneDaily}.
 * @returns the {@link OneDailyDelivery} for {@link describeDelivery}.
 */
export const oneDailyDelivery = (
  event: Pick<SetupEvent, 'oneDaily'>,
  config: Pick<EventConfig, 'frequency'>,
  channels: NotificationChannel[],
  frequency: NotificationFrequency,
  tenant: OneDailyDelivery,
): OneDailyDelivery => {
  if (!tenant || !event.oneDaily || config.frequency !== null) return null;
  if (frequency === 'weekly' || frequency === 'off') return null;
  const hasMail = channels.includes('email') || channels.includes('digest');
  if (!channels.includes('bell') || !hasMail) return null;
  return tenant;
};

/**
 * What one row will actually do, walking the three levels the backend resolves
 * through: the row's own cadence, then the tenant default, then the platform
 * default shipped with the template.
 *
 * @param pinned - the row's own cadence, or `null` when it inherits.
 * @param tenantDefault - the tenant-wide cadence, or `''` when unset.
 * @param platform - the cadence shipped with this event's template.
 * @returns the cadence this row runs on right now.
 */
export const resolveFrequency = (
  pinned: NotificationFrequency | null,
  tenantDefault: NotificationTenantDefaultFrequency,
  platform: NotificationFrequency,
): NotificationFrequency => pinned ?? (tenantDefault || platform);

/** The three things an admin controls per event. */
export interface EventConfig {
  /** Does this event fire at all for this tenant? */
  enabled: boolean;
  /**
   * Channels this event is *allowed* to use — always a subset of
   * {@link SetupEvent.availableChannels}, and the exact list the API's
   * `channel_override` speaks.
   *
   * Not the surfaces it will fire on today: the API resolves those from the
   * cadence (a batched event mails on `digest`, an immediate one on `email`),
   * so its `effective.channels` names surfaces this list must never copy. A
   * channel the platform does not ship is a widening the API refuses outright.
   */
  channels: NotificationChannel[];
  /**
   * How often it notifies, or `null` to inherit
   * {@link MasterSwitches.defaultFrequency} — the tenant-wide default. `null`
   * is what the grid renders as "Default"; anything else is "Custom".
   */
  frequency: NotificationFrequency | null;
}

/** One catalogue row, flattened for the grid. */
export interface SetupEvent {
  /** Stable dotted event key — the only identifier the API and grid share. */
  key: string;
  name: string;
  description: string;
  domain: string;
  domainLabel: string;
  subdomain: string;
  subdomainLabel: string;
  /** Channels the platform ships this event on. A tenant may narrow, never widen. */
  availableChannels: NotificationChannel[];
  /**
   * Cadence shipped with the template. Only reached when the tenant has set no
   * blanket default — it is the last of the three resolution levels, never a
   * value the grid writes.
   */
  platformFrequency: NotificationFrequency;
  /** What the platform ships — what "reset to default" restores. */
  defaults: EventConfig;
  /** Shipped defaults with this tenant's override applied. */
  effective: EventConfig;
  /** The tenant override row's id, when one exists. */
  overrideId: number | null;
  isOverridden: boolean;
  /** False for legally/contractually required events — the On toggle locks. */
  canDisable: boolean;
  /**
   * Baseline routing is on and this event's rule reaches the customer side:
   * people reached without a named buyer or owner get one email a day, unless
   * the row sets its own frequency (`row.baseline_daily`).
   */
  baselineDaily: boolean;
  /**
   * The company's one daily digest carries this event's email (`row.one_daily`).
   * False for critical and required events, a row with its own frequency, a
   * weekly or Off row, and a row with no bell or no mail channel.
   */
  oneDaily: boolean;
  /** Which side of the portal it reaches. */
  reaches: EventReach;
  /** False when the template is soft-disabled platform-wide. */
  isActive: boolean;
  /** False when `requires_module` names a module this tenant has switched off. */
  moduleEnabled: boolean;
}

/** One collapsible module section. */
export interface SetupDomain {
  domain: string;
  label: string;
  events: SetupEvent[];
}

/** A `{value,label}` pair from the API's choice sets. */
export interface SetupChoice {
  value: string;
  label: string;
}

/** The whole parsed catalogue, ready to render. */
export interface SetupCatalogue {
  domains: SetupDomain[];
  eventCount: number;
  overriddenCount: number;
  /** Server truth for "may this user write config?" — mirrors IsHelixOrCompanyAdmin. */
  canConfigure: boolean;
  /**
   * The real cadences. Both selectors render their own inherit option on top
   * of this — the API's inherit sentinel is `''`, which Radix cannot hold as
   * an item value, so it never survives parsing.
   */
  frequencyChoices: SetupChoice[];
  /**
   * The company the server resolved this catalogue to — `meta.company`, and the
   * id every write echoes back as its scope claim. `null` when the payload
   * carried no scope block (an older backend), which is the only case where the
   * screen states nothing about scope.
   */
  companyId: number | null;
  /** `meta.company_name` — the name the header states, never an inferred one. */
  companyName: string;
  /** `meta.baseline_routing` — owner first, else the department once a day. */
  baselineRouting: boolean;
  /** `meta.one_daily_digest` — everyone gets one email a day at 07:00. */
  oneDailyDigest: boolean;
  /** How the server arrived at that company. `''` when it did not say. */
  scopeSource: NotificationScopeSource | '';
}

/** How each resolution finishes the header's sentence. */
const SCOPE_REASONS: Record<NotificationScopeSource | '', string> = {
  site: ' — the site selected in the top bar.',
  company: ' — the company selected in the top bar.',
  home: ' — your own company, because no site is selected in the top bar.',
  // The server named a company but not how it got there: state the company and
  // claim nothing further.
  '': '.',
};

/**
 * The header's one-line statement of whose configuration is on screen.
 *
 * The screen used to make no claim about scope at all, which is what let a
 * platform operator configure Helix Platform while reading a division's name
 * off the top bar. Naming the company is half of the fix; naming *how* it was
 * resolved is the other half — "the site selected in the top bar" and "your own
 * company" are the two states that used to be indistinguishable.
 *
 * @param companyName - the resolved `meta.company_name`.
 * @param source - the resolved `meta.company_scope_source`.
 * @returns the sentence to render, or `null` when the server named no company
 *   and the screen therefore has nothing it can honestly state.
 */
export function describeScope(
  companyName: string,
  source: NotificationScopeSource | '',
): string | null {
  if (!companyName) return null;
  return `Configuring ${companyName}${SCOPE_REASONS[source]}`;
}

/** How many events run on a custom cadence rather than the tenant default. */
export interface FrequencySummary {
  /** Rows with their own frequency, staged edits included. */
  customised: number;
  total: number;
}

/** Master switches the grid header renders, parsed defensively from the API row. */
export interface MasterSwitches {
  emailsEnabled: boolean;
  bellEnabled: boolean;
  digestEnabled: boolean;
  /**
   * Tenant-wide cadence every event inherits unless it has its own. `''` means
   * the tenant has set none, so each event uses its own platform default.
   */
  defaultFrequency: NotificationTenantDefaultFrequency;
  quietHoursEnabled: boolean;
  /** `HH:MM`, or `''` when unset. */
  quietHoursStart: string;
  quietHoursEnd: string;
  quietHoursApplyToCritical: boolean;
  /**
   * Default cap on distinct notification emails sent to any one supplier
   * per day. Counted per send, not per recipient: a summary reaching ten people
   * at the same company spends one. `0` means unlimited.
   */
  defaultMaxEmailsPerDay: number;
  /** Most notification emails one person is sent in a day. `0` means unlimited. */
  maxEmailsPerUserPerDay: number;
  /**
   * CR-7281: the install's chosen daily procurement summary style. `''` means
   * never chosen here, so the instance default decides.
   */
  procurementSummaryStyle: '' | 'helix' | 'classic';
  /** The style actually in force, instance default included. */
  effectiveProcurementSummaryStyle: 'helix' | 'classic';
}

/** One supplier's exception to the site default. */
export interface SupplierSendBudget {
  supplier: number;
  supplierName: string;
  /** `0` means "no exception — use the site default". */
  maxEmailsPerDay: number;
}

/** What a limit actually held back, so it can be accounted for after the fact. */
export interface WithheldSend {
  supplier: number;
  supplierName: string;
  /** Distinct sends withheld, not deliveries. */
  sends: number;
  recipients: number;
  /** `YYYY-MM-DD`. */
  lastDate: string;
  cap: number;
}
